import type { ClientRecord } from "./mockClients"
import {
	getOrders,
	getOrderById,
	extractCustomerTagValue,
	extractCashDiscountPercent,
	parseItemOptions,
	formatTotal,
	type OrderRecord,
} from "./orders"
import customerGroupsIndex from "./customer_groups_index.json"

// ----------------------------------------------------------------------------

export interface ProductRecord {
	uid?: number
	code: string
	sku?: string
	price: string
	numericPrice: number
	name?: string
	description?: string
	imageUrl?: string
	brandName?: string
	categories?: string[]
	tags?: string[]
	colors?: string[]
	temperatures?: string[]
	sizes?: string[]
	stockCount?: number
	unlimited?: number
}

export const fallbackProducts: ProductRecord[] = [
	{
		code: "GLS001",
		sku: "GLS001-B2B",
		price: "€156.00",
		numericPrice: 156,
		name: "Lampada a sospensione GLS001",
		description:
			"Sospensione lineare per ambienti contract con finitura nera e diffusore satinato.",
		brandName: "Gea Luce",
		categories: ["Sospensioni", "Indoor"],
		tags: ["Nero", "Bianco", "3000K", "4000K"],
		colors: ["Nero", "Bianco"],
		temperatures: ["3000K", "4000K"],
		sizes: ["Small", "Medium"],
	},
	{
		code: "GLS020",
		sku: "GLS020-B2B",
		price: "€116.00",
		numericPrice: 116,
		name: "Applique GLS020",
		description:
			"Applique minimal per corridoi e hotellerie con emissione indiretta.",
		brandName: "Gea Luce",
		categories: ["Applique", "Indoor"],
		tags: ["Bianco", "Ottone", "3000K"],
		colors: ["Bianco", "Ottone"],
		temperatures: ["3000K"],
	},
	{
		code: "GLS021",
		sku: "GLS021-B2B",
		price: "€124.00",
		numericPrice: 124,
		name: "Plafoniera GLS021",
		description: "Plafoniera compatta per forniture residenziali e retail.",
		brandName: "Gea Luce",
		categories: ["Plafoniere", "Indoor"],
		tags: ["Nero", "4000K", "6000K"],
		colors: ["Nero"],
		temperatures: ["4000K", "6000K"],
	},
	{
		code: "GLTS021",
		sku: "GLTS021-B2B",
		price: "€129.00",
		numericPrice: 129,
		name: "Faretto tecnico GLTS021",
		description:
			"Corpo orientabile per progetti B2B con elevata resa cromatica.",
		brandName: "Gea Luce",
		categories: ["Faretti", "Tecnico"],
		tags: ["Bianco", "Nero", "3000K", "4000K"],
		colors: ["Bianco", "Nero"],
		temperatures: ["3000K", "4000K"],
	},
	{
		code: "GLL030",
		sku: "GLL030-B2B",
		price: "€98.00",
		numericPrice: 98,
		name: "Segnapasso GLL030",
		description:
			"Segnapasso da parete a bassa profondita per corridoi e hospitality.",
		brandName: "Gea Luce",
		categories: ["Segnapasso", "Indoor"],
		tags: ["Grigio", "Nero", "3000K"],
		colors: ["Grigio", "Nero"],
		temperatures: ["3000K"],
	},
	{
		code: "GLP050",
		sku: "GLP050-B2B",
		price: "€189.00",
		numericPrice: 189,
		name: "Proiettore GLP050",
		description:
			"Proiettore professionale per vetrine e showroom con ottica stretta.",
		brandName: "Gea Luce",
		categories: ["Proiettori", "Retail"],
		tags: ["Nero", "4000K"],
		colors: ["Nero"],
		temperatures: ["4000K"],
	},
]

export function filterProductsByKeywords(
	productsList: ProductRecord[],
	keywords: string,
): ProductRecord[] {
	const query = keywords.trim().toLowerCase()
	if (!query) {
		return productsList
	}

	return productsList.filter((product) => {
		const code = (product.code || "").toLowerCase()
		const sku = (product.sku || "").toLowerCase()
		const name = (product.name || "").toLowerCase()
		const description = (product.description || "").toLowerCase()
		const brand = (product.brandName || "").toLowerCase()
		const tags = (product.tags || []).join(" ").toLowerCase()

		return (
			code.includes(query) ||
			sku.includes(query) ||
			name.includes(query) ||
			description.includes(query) ||
			brand.includes(query) ||
			tags.includes(query)
		)
	})
}

export const PRODUCTS_FALLBACK_ERROR_MESSAGE =
	"API prodotti non disponibile al momento. Sto mostrando i prodotti di fallback locale."
export const PRODUCTS_FALLBACK_TOAST_TITLE =
	"Stai lavorando con i prodotti locali di fallback"
export const PRODUCTS_FALLBACK_TOAST_DESC =
	"Impossibile contattare il server ngrok."

// This file acts as the centralized API client layer for integrating backend services.
// It uses the ngrok-free.dev base URL provided for external B2B integration.
//
// Error handling:
// - Caught exceptions are logged following the standard: console.error("[Context] description:", error)
// - Functions will throw the error up after logging to let components handle loading/error states in the UI.

const API_BASE_URL = "https://gealuce.no-gravity.dev/api/integrations/b2b"
// const API_BASE_URL = "http://localhost:9001/integrations/b2b";
const PRODUCT_IMAGE_BASE_URL = "https://egress.storeden.net/gallery"

// ----------------------------------------------------------------------------

async function fetchJson<T>(path: string): Promise<T> {
	const url = `${API_BASE_URL}${path}`

	//console.info("[API] Request started:", { url, path });

	const response = await fetch(url)
	const contentType = response.headers.get("content-type") ?? ""
	const ngrokErrorCode = response.headers.get("ngrok-error-code") ?? ""
	const responseText = await response.text()

	/*
    console.info("[API] Response received:", {
        url,
        status: response.status,
        statusText: response.statusText,
        contentType,
        ngrokErrorCode,
    });
    */

	if (!response.ok) {
		console.error("[API] Request failed:", {
			url,
			status: response.status,
			statusText: response.statusText,
			contentType,
			ngrokErrorCode,
			bodyPreview: responseText.slice(0, 500),
		})

		throw new Error(
			`HTTP error! status: ${response.status}${
				ngrokErrorCode ? ` (${ngrokErrorCode})` : ""
			}`,
		)
	}

	if (!contentType.includes("application/json")) {
		console.error("[API] Invalid response content type:", {
			url,
			status: response.status,
			contentType,
			bodyPreview: responseText.slice(0, 500),
		})

		throw new Error("Invalid API response: expected JSON payload")
	}

	try {
		return JSON.parse(responseText) as T
	} catch (error) {
		console.error("[API] JSON parse failed:", {
			url,
			bodyPreview: responseText.slice(0, 500),
			error,
		})
		throw error
	}
}

interface RawOrder {
	orderId: string
	total: number
	orderDate: string
	status: number
	billingFullname: string
	billingEmail: string
	shortId: number
}

interface RawCustomer {
	id?: string | number
	uid?: string | number
	customerId?: string | number
	name?: string
	fullName?: string
	fullname?: string
	email?: string
	phone?: string
	ordersCount?: number
	totalSpent?: string | number
	status?: string
	addressLine1?: string
	address?: string
	city?: string
	province?: string
	postalCode?: string
	zip?: string
	notes?: string
	note?: string
	role?: string
	isAgent?: boolean
	customerType?: string
	type?: string
	groupName?: string
	group?: string
	tags?: string[]
	// Added optional fields for mapping
	code?: string
	state?: string
	country?: string
	taxNumber?: string
}

export const KNOWN_CUSTOMER_GROUPS: Record<string, string> =
	customerGroupsIndex as Record<string, string>

const customerGroupsCache: Record<string, string> = { ...KNOWN_CUSTOMER_GROUPS }

export function getGroupNameById(groupId?: string | number | null, defaultValue?: string): string | undefined {
	if (!groupId) return defaultValue
	const idStr = String(groupId).trim()
	return customerGroupsCache[idStr] ?? defaultValue
}

/**
 * Estrae e calcola la percentuale di sconto totale dal nome del gruppo.
 * Se sono presenti piu percentuali sommate (es. "Sconto 50%+10%+08%"), calcola la somma totale:
 * 50 + 10 + 8 = 68%
 */
export function parseDiscountPercentFromGroupName(groupName?: string): number | undefined {
	if (!groupName) return undefined

	// Caso percentuali multiple con simbolo % (es. "Sconto 50%+10%+08%" -> [50, 10, 8] => 68)
	const percentMatches = Array.from(groupName.matchAll(/(\d+)\s*%/g))
	if (percentMatches.length > 1) {
		const total = percentMatches.reduce((sum, m) => sum + parseInt(m[1]!, 10), 0)
		return total > 0 ? total : undefined
	}

	// Caso con '+' ma senza '%' su ogni termine (es. "Sconto 50+10+8%")
	if (groupName.includes("+")) {
		const clean = groupName.replace(/sconto/i, "").trim()
		const parts = clean
			.split("+")
			.map((p) => parseInt(p.replace(/[^0-9]/g, ""), 10))
			.filter((n) => !isNaN(n))
		if (parts.length > 0) {
			const total = parts.reduce((sum, n) => sum + n, 0)
			return total > 0 ? total : undefined
		}
	}

	// Caso percentuale singola (es. "Sconto 65%" => 65)
	if (percentMatches.length === 1) {
		return parseInt(percentMatches[0]![1]!, 10)
	}

	return undefined
}

export async function fetchCustomerGroups(): Promise<Array<{ id: string; name: string }>> {
	try {
		const data = await fetchJson<any[]>("/customers/groups/list").catch(() => [])
		const list = Array.isArray(data) ? data : []
		for (const g of list) {
			const id = String(g.id ?? g._id ?? g.group_id ?? "")
			const name = g.name ?? g.title ?? g.group_name ?? ""
			if (id && name) {
				customerGroupsCache[id] = name
			}
		}
		return list
	} catch (err) {
		console.warn("[API] Failed to fetch customer groups:", err)
		return []
	}
}

const LOCAL_CUSTOMER_INDEX_KEY = "gea_luce_custom_customer_tags"

export function getLocalCustomerIndex(): Record<
	string,
	{
		tags?: string[]
		city?: string
		code?: string
		zip?: string
		address?: string
		phone?: string
		province?: string
		state?: string
		country?: string
	}
> {
	if (typeof window === "undefined") return {}
	try {
		const raw = localStorage.getItem(LOCAL_CUSTOMER_INDEX_KEY)
		return raw ? JSON.parse(raw) : {}
	} catch {
		return {}
	}
}

export function saveLocalCustomerIndexEntry(
	idOrEmail: string,
	data: {
		tags?: string[]
		city?: string
		code?: string
		zip?: string
		address?: string
		phone?: string
		province?: string
		state?: string
		country?: string
	},
): void {
	if (typeof window === "undefined" || !idOrEmail) return
	try {
		const current = getLocalCustomerIndex()
		current[idOrEmail] = { ...current[idOrEmail], ...data }
		localStorage.setItem(LOCAL_CUSTOMER_INDEX_KEY, JSON.stringify(current))
	} catch (err) {
		console.warn("[API] Failed to save local customer index entry:", err)
	}
}

function mapRawCustomer(inputRaw: RawCustomer | any, index: number): ClientRecord {
	const raw =
		inputRaw &&
		typeof inputRaw === "object" &&
		"data" in inputRaw &&
		inputRaw.data &&
		typeof inputRaw.data === "object"
			? { ...inputRaw, ...inputRaw.data }
			: inputRaw || {}

	const idStr = String(
		raw.id ?? raw.uid ?? raw.customerId ?? `customer_${index}`,
	)
	const nameStr = raw.name || raw.fullName || raw.fullname || ""
	const emailStr = raw.email || ""
	const phoneStr = raw.phone || ""
	let ordersCountNum =
		typeof raw.ordersCount === "number"
			? raw.ordersCount
			: typeof raw.orderCount === "number"
			? raw.orderCount
			: 0

	let totalSpentStr = "€0.00"
	if (typeof raw.totalSpent === "number") {
		totalSpentStr = `€${raw.totalSpent.toFixed(2)}`
	} else if (typeof raw.totalSpend === "number") {
		totalSpentStr = `€${raw.totalSpend.toFixed(2)}`
	} else if (
		typeof raw.totalSpent === "string" &&
		raw.totalSpent.trim().length > 0
	) {
		totalSpentStr = raw.totalSpent
	} else if (
		typeof raw.totalSpend === "string" &&
		raw.totalSpend.trim().length > 0
	) {
		totalSpentStr = raw.totalSpend
	}

	if (Array.isArray(raw.orders)) {
		const isOrderCancelled = (status: unknown) => {
			const s = Number(status)
			if (s === 6 || s === 7) return true
			if (typeof status === "string") {
				const lower = status.toLowerCase().trim()
				if (
					lower === "cancelled" ||
					lower === "annullato" ||
					lower === "aborted" ||
					lower === "archived"
				)
					return true
			}
			return false
		}
		const activeOrders = raw.orders.filter((o: any) => !isOrderCancelled(o.status))
		ordersCountNum = activeOrders.length
		const sumActive = activeOrders.reduce(
			(sum: number, o: any) => sum + (Number(o.total) || 0),
			0,
		)
		totalSpentStr = `€${sumActive.toFixed(2)}`
	}

	let statusVal: "Attivo" | "Premium" | "Inattivo" = "Attivo"
	if (
		raw.status === "Attivo" ||
		raw.status === "Premium" ||
		raw.status === "Inattivo"
	) {
		statusVal = raw.status
	}

	const localIndex = getLocalCustomerIndex()
	const localData =
		localIndex[idStr] ||
		(emailStr ? localIndex[emailStr.toLowerCase().trim()] : undefined)

	const tagsList =
		Array.isArray(raw.tags) && raw.tags.length > 0
			? raw.tags.filter(Boolean)
			: (localData?.tags ?? undefined)

	const phoneValue = raw.phone || raw.mobile || localData?.phone || phoneStr
	const addressValue =
		raw.addressLine1 ||
		raw.address ||
		raw.addresses?.[0]?.address1 ||
		localData?.address ||
		""
	const cityValue = raw.city || raw.addresses?.[0]?.city || localData?.city || ""
	const postalCodeValue =
		raw.postalCode || raw.zip || raw.addresses?.[0]?.zip || localData?.zip || ""
	const codeValue =
		raw.code || raw.taxNumber || raw.tax_number || localData?.code || ""
	const provinceValue = raw.province || raw.state || (raw.addresses?.[0]?.state ? String(raw.addresses[0].state) : "") || localData?.province || ""
	const countryValue = raw.country || (raw.addresses?.[0]?.country ? String(raw.addresses[0].country) : "") || localData?.country || ""

	const resolvedGroupName =
		raw.groupName || (raw.group ? getGroupNameById(raw.group) : undefined)
	const discountPercent = resolvedGroupName
		? parseDiscountPercentFromGroupName(resolvedGroupName)
		: undefined

	return {
		id: idStr,
		name: nameStr,
		email: emailStr,
		phone: phoneValue,
		ordersCount: ordersCountNum,
		totalSpent: totalSpentStr,
		status: statusVal,
		addressLine1: addressValue,
		city: cityValue,
		province: provinceValue,
		postalCode: postalCodeValue,
		notes: raw.notes || raw.note || "",
		role: raw.role || undefined,
		isAgent:
			raw.isAgent === true ||
			(Array.isArray(tagsList) &&
				tagsList.some(
					(t) =>
						typeof t === "string" && t.trim().toLowerCase() === "isagent-si",
				)),
		customerType: raw.customerType || raw.type || undefined,
		group: raw.group ? String(raw.group) : undefined,
		groupName: resolvedGroupName,
		discountPercent: discountPercent,
		cashDiscountPercent: extractCashDiscountPercent(tagsList),
		tags: tagsList,
		code: codeValue,
		country: raw.state || raw.country || "",
	}
}

/**
 * Fetches the list of orders from the B2B integration endpoint.
 * GET /integrations/b2b/orders
 */
export async function fetchOrders(
	knownCustomers?: ClientRecord[],
	agent = "",
): Promise<OrderRecord[]> {
	try {
		const limit = 100
		let skip = 0
		let allRawOrders: RawOrder[] = []
		let hasMore = true
		const agentParam = agent !== "" ? `&agent=${agent}` : ""

		while (hasMore) {
			const data = await fetchJson<RawOrder[]>(
				`/orders?limit=${limit}&skip=${skip}${agentParam}`,
			)
			if (data && data.length > 0) {
				allRawOrders = allRawOrders.concat(data)
				if (data.length < limit) {
					hasMore = false
				} else {
					skip += limit
				}
			} else {
				hasMore = false
			}
		}

		console.info("[API] fetchOrders success:", {
			count: allRawOrders.length,
		})
		const customersList = knownCustomers ?? []
		const customersByEmail = new Map(
			customersList.map((c) => [c.email.toLowerCase(), c]),
		)
		const customersByName = new Map(
			customersList.filter((c) => c.name).map((c) => [c.name.toLowerCase(), c]),
		)

		const localOrdersList: OrderRecord[] = getOrders()
		const localOrdersMap = new Map<string, OrderRecord>(
			localOrdersList.map((o) => [o.id, o]),
		)

		const activeRawOrders = allRawOrders.filter((raw) => {
			const s = Number(raw.status)
			return s !== 6 && s !== 7
		})

		return activeRawOrders.map((raw) => {
			const rawEmail = (raw.billingEmail || "").toLowerCase()
			const rawName = (raw.billingFullname || "").toLowerCase()

			const client =
				customersByEmail.get(rawEmail) ||
				(rawName ? customersByName.get(rawName) : undefined)

			const localOrder = localOrdersMap.get(raw.orderId)
			return {
				id: raw.orderId,
				clientId: client ? client.id : (localOrder?.clientId || ""),
				date: raw.orderDate ? raw.orderDate.slice(0, 10) : (localOrder?.date || ""),
				customerName: raw.billingFullname || localOrder?.customerName || "",
				email: raw.billingEmail || localOrder?.email || "",
				total: `€${(raw.total || 0).toFixed(2)}`,
				items: [],
				status: raw.status,
			}
		})
	} catch (error) {
		console.error("[API] fetchOrders failed:", error)
		throw error
	}
}

/**
 * Enriches orders with clientId by matching order email or customer name against a customer list.
 */
export function enrichOrdersWithCustomers(
	orders: OrderRecord[],
	customers: ClientRecord[],
): OrderRecord[] {
	if (!customers || customers.length === 0) return orders
	const customersByEmail = new Map(
		customers
			.filter((c) => c.email)
			.map((c) => [c.email.toLowerCase().trim(), c]),
	)
	const customersByName = new Map(
		customers
			.filter((c) => c.name)
			.map((c) => [c.name.toLowerCase().trim(), c]),
	)

	return orders.map((order) => {
		if (order.clientId) return order
		const emailKey = (order.email || "").toLowerCase().trim()
		const nameKey = (order.customerName || "").toLowerCase().trim()
		const matchedClient =
			(emailKey ? customersByEmail.get(emailKey) : undefined) ||
			(nameKey ? customersByName.get(nameKey) : undefined)

		return matchedClient ? { ...order, clientId: matchedClient.id } : order
	})
}

/**
 * Fetches the list of customers from the B2B integration endpoint.
 * GET /integrations/b2b/customers
 */
export async function fetchCustomers(agent = ""): Promise<ClientRecord[]> {
	try {
		const limit = 100
		let allRawCustomers: RawCustomer[] = []
		let currentPage = 1
		let hasMore = true
		const agentParam = agent !== "" ? agent : ""

		while (hasMore) {
			const data = await fetchJson<RawCustomer[]>(
				`/customers?limit=${limit}&page=${currentPage}&agent=${agentParam}`,
			).catch((err) => {
				console.warn(
					`[API] Failed to fetch customers page ${currentPage}:`,
					err,
				)
				return [] as RawCustomer[]
			})

			if (data && data.length > 0) {
				allRawCustomers = allRawCustomers.concat(data)
				if (data.length < limit) {
					hasMore = false
				} else {
					currentPage++
				}
			} else {
				hasMore = false
			}
		}

		console.info("[API] fetchCustomers success:", {
			count: allRawCustomers.length,
		})
		if (allRawCustomers.length === 0) {
			console.warn("[API] fetchCustomers returned empty list from server.")
			return []
		}
		return allRawCustomers.map((raw, index) => mapRawCustomer(raw, index))
	} catch (error) {
		console.error("[API] fetchCustomers failed:", error)
		throw error
	}
}

/**
 * Fetches a single customer by ID from the B2B integration endpoint.
 * GET /integrations/b2b/customers/:customerId
 */
export async function fetchCustomerById(
	id: string,
): Promise<ClientRecord | null> {
	try {
		const data = await fetchJson<RawCustomer[] | RawCustomer>(
			`/customers/${encodeURIComponent(id)}`,
		)
		let client: ClientRecord | null = null
		if (Array.isArray(data)) {
			if (data.length > 0) client = mapRawCustomer(data[0]!, 0)
		} else if (data && typeof data === "object") {
			client = mapRawCustomer(data, 0)
		}
		console.info("[API] fetchCustomerById success:", { id, data: client })
		return client
	} catch (error) {
		console.error(
			"[API] GET /customers/:customerId failed, trying fallback search:",
			error,
		)
		try {
			const data = await fetchJson<RawCustomer[] | RawCustomer>(
				`/customers?id=${encodeURIComponent(id)}`,
			)
			let client: ClientRecord | null = null
			if (Array.isArray(data)) {
				if (data.length > 0) client = mapRawCustomer(data[0]!, 0)
			} else if (data && typeof data === "object") {
				client = mapRawCustomer(data, 0)
			}
			console.info("[API] fetchCustomerById fallback success:", { id, data: client })
			return client
		} catch {
			// ignore
		}

		return null
	}
}

interface RawProduct {
	uid: number
	code: string
	sku: string
	price: number
	finalPrice: number
	status: number
	title: string
	imageId?: string
	brandName?: string
	categoriesName?: string[]
	tags?: string[]
	colors?: string[]
	temperatures?: string[]
	sizes?: string[]
	stockCount?: number
	stock_count?: number
	quantity?: number
	unlimited?: number
}

function parseVariantsFromRawProduct(raw: RawProduct): {
	colors: string[]
	temperatures: string[]
	sizes: string[]
} {
	const colorsSet = new Set<string>()
	const tempsSet = new Set<string>()
	const sizesSet = new Set<string>()

	if (Array.isArray(raw.colors)) {
		raw.colors.forEach((c) => c && colorsSet.add(c.trim()))
	}
	if (Array.isArray(raw.temperatures)) {
		raw.temperatures.forEach((t) => t && tempsSet.add(t.trim()))
	}
	if (Array.isArray(raw.sizes)) {
		raw.sizes.forEach((s) => s && sizesSet.add(s.trim()))
	}

	const tags = raw.tags ?? []
	tags.forEach((tag) => {
		const t = tag.toLowerCase().trim()
		if (t.startsWith("colore-")) {
			const colorVal = tag.slice("colore-".length).trim()
			if (colorVal) {
				colorsSet.add(colorVal.charAt(0).toUpperCase() + colorVal.slice(1))
			}
		} else if (t.startsWith("temperatura-colore-")) {
			const tempVal = tag
				.slice("temperatura-colore-".length)
				.trim()
				.toUpperCase()
			if (tempVal) {
				tempsSet.add(tempVal)
			}
		} else if (
			t.includes("3000k") ||
			t.includes("4000k") ||
			t.includes("2700k") ||
			t.includes("6000k")
		) {
			const match = t.match(/\b(2700k|3000k|4000k|5000k|6000k)\b/i)
			if (match && match[1]) {
				tempsSet.add(match[1].toUpperCase())
			}
		} else if (
			t.startsWith("taglia-") ||
			t.startsWith("dimensione-") ||
			t.startsWith("misura-")
		) {
			const sizeVal = tag.replace(/^(taglia-|dimensione-|misura-)/, "").trim()
			if (sizeVal) {
				sizesSet.add(sizeVal.charAt(0).toUpperCase() + sizeVal.slice(1))
			}
		}
	})

	return {
		colors: Array.from(colorsSet),
		temperatures: Array.from(tempsSet),
		sizes: Array.from(sizesSet),
	}
}

function deriveCategoriesFromRaw(raw: RawProduct): string[] {
	if (raw.categoriesName && raw.categoriesName.length > 0) {
		return raw.categoriesName
	}

	const title = (raw.title || "").toUpperCase()
	const code = (raw.code || "").toUpperCase()

	const categories: string[] = []

	if (
		title.includes("PIANTANA") ||
		title.includes(" TERRA") ||
		code.includes("PIA")
	) {
		categories.push("Lampade da Terra")
	}
	if (
		title.includes("SOSPENSIONE") ||
		title.includes(" S/") ||
		title.endsWith(" S") ||
		code.includes("SOS") ||
		code.startsWith("GSOS") ||
		code.endsWith(" S")
	) {
		categories.push("Sospensioni")
	}
	if (
		title.includes("APPLIQUE") ||
		title.endsWith(" A") ||
		code.includes("APP") ||
		code.startsWith("FECA")
	) {
		categories.push("Applique")
	}
	if (
		title.includes("PLAFONIERA") ||
		title.endsWith(" P") ||
		code.includes("PLA") ||
		code.startsWith("FECP")
	) {
		categories.push("Plafoniere")
	}
	if (title.includes("TAVOLO") || title.includes("DESK")) {
		categories.push("Lampade da Tavolo")
	}
	if (
		title.includes("INCASSO") ||
		title.includes("FARETTO") ||
		code.includes("INC")
	) {
		categories.push("Incassi")
	}
	if (title.includes("OUTDOOR") || title.includes("ESTERNO")) {
		categories.push("Outdoor")
	}
	if (
		title.includes("STRIP") ||
		title.includes("STP") ||
		code.startsWith("GSTP")
	) {
		categories.push("Strisce LED")
	}

	if (categories.length === 0) {
		categories.push("Indoor")
	}

	return categories
}

function mapRawProduct(raw: RawProduct): ProductRecord {
	const categories = deriveCategoriesFromRaw(raw)
	const tags = raw.tags ?? []
	const imageId = raw.imageId?.trim()
	const imageUrl = imageId
		? /^https?:\/\//.test(imageId)
			? imageId
			: `${PRODUCT_IMAGE_BASE_URL}/${imageId}`
		: undefined

	const parsedVariants = parseVariantsFromRawProduct(raw)

	return {
		uid: raw.uid,
		code: raw.code,
		sku: raw.sku,
		price: formatTotal(raw.finalPrice),
		numericPrice: raw.finalPrice,
		name: raw.title,
		description: categories.join(" > ") || tags.join(", ") || "",
		imageUrl,
		brandName: raw.brandName || "Gea Luce",
		categories,
		tags,
		colors: parsedVariants.colors,
		temperatures: parsedVariants.temperatures,
		sizes: parsedVariants.sizes,
		stockCount: raw.stockCount ?? raw.stock_count ?? raw.quantity,
		unlimited: raw.unlimited,
	}
}

interface ProductsApiResponse {
	totalPages: number
	productsPerPage: number
	totalItems: number
	currentPageNumber: number
	products: RawProduct[]
}

export interface ProductsResponse {
	totalPages: number
	productsPerPage: number
	totalItems: number
	currentPageNumber: number
	products: ProductRecord[]
}

export async function fetchProductStock(
	identifier: number | string,
): Promise<{ stockCount: number; unlimited?: number } | null> {
	try {
		// 1. Try B2B products/stock endpoint directly without loud error logs if 404
		const res = await fetch(
			`${API_BASE_URL}/products/stock/${encodeURIComponent(identifier)}`,
		)
		if (res.ok) {
			const data = await res.json()
			if (typeof data?.stockCount === "number") {
				return {
					stockCount: data.stockCount,
					unlimited: data.unlimited,
				}
			}
		}

		// 2. Fallback for local standalone Bun server if running on port 5001
		const origin =
			typeof window !== "undefined" && window.location?.origin
				? window.location.origin
				: "http://localhost:5001"
		const localRes = await fetch(
			`${origin}/api/product-stock/${encodeURIComponent(identifier)}`,
		)
		if (localRes.ok) {
			const localData = await localRes.json()
			if (typeof localData?.stockCount === "number") {
				return {
					stockCount: localData.stockCount,
					unlimited: localData.unlimited,
				}
			}
		}
	} catch {
		// Silently fallback if network / stock endpoint is not available
	}
	return null
}


/**
 * Fetches the list of products from the B2B integration endpoint.
 * GET /integrations/b2b/products
 *
 * Supports optional keyword filtering via "?keywords=skuprodotto" and pagination via "?page=number".
 *
 * @param keywords Optional SKU or keywords to search for products
 * @param page Optional page number (1-indexed)
 */
export async function fetchProducts(
	keywords?: string,
	page: number = 1,
	limit: number = 20,
): Promise<ProductsResponse> {
	try {
		let path = "/products"
		const params = new URLSearchParams()

		const query = keywords?.trim()
		if (query) {
			params.append("keywords", query)
		}
		if (page && page > 1) {
			params.append("page", page.toString())
		}
		if (limit && limit > 0) {
			params.append("limit", limit.toString())
		}

		const queryString = params.toString()
		if (queryString) {
			path += `?${queryString}`
		}

		const response = await fetchJson<ProductsApiResponse>(path)
		const rawProducts = response?.products || []
		console.info("[API] fetchProducts success:", {
			count: rawProducts.length,
			totalPages: response?.totalPages,
			totalItems: response?.totalItems,
			productsPerPage: response?.productsPerPage,
			currentPageNumber: response?.currentPageNumber,
			products: rawProducts,
		})

		const mappedProducts = await Promise.all(
			rawProducts.map(async (raw) => {
				const product = mapRawProduct(raw)
				if (product.stockCount === undefined && raw.uid) {
					const stockData = await fetchProductStock(raw.uid)
					if (stockData) {
						product.stockCount = stockData.stockCount
						if (stockData.unlimited !== undefined) {
							product.unlimited = stockData.unlimited
						}
					}
				}
				return product
			}),
		)

		return {
			totalPages: response?.totalPages || 1,
			productsPerPage: response?.productsPerPage || limit || 20,
			totalItems: response?.totalItems || rawProducts.length,
			currentPageNumber: response?.currentPageNumber || page || 1,
			products: mappedProducts,
		}
	} catch (error) {
		console.error("[API] fetchProducts failed:", error)
		throw error
	}
}

const productFamilyCache = new Map<
	string,
	{ products: ProductRecord[]; timestamp: number }
>()
const PRODUCT_FAMILY_CACHE_TTL = 3 * 60 * 1000 // 3 minutes

export async function fetchProductFamily(
	fatherTag: string,
): Promise<ProductRecord[]> {
	const normalizedTag = fatherTag.trim().toLowerCase()
	const cached = productFamilyCache.get(normalizedTag)
	if (cached && Date.now() - cached.timestamp < PRODUCT_FAMILY_CACHE_TTL) {
		return cached.products
	}

	try {
		let rawProducts: RawProduct[] = []

		try {
			// 1. Try dedicated /products/family/:fatherTag endpoint
			const response = await fetchJson<{ products?: RawProduct[] } | RawProduct[]>(
				`/products/family/${encodeURIComponent(normalizedTag)}`,
			)
			if (Array.isArray(response)) {
				rawProducts = response
			} else if (response && Array.isArray(response.products)) {
				rawProducts = response.products
			}
		} catch {
			// 2. Fallback to /products?keywords=*&tags=:fatherTag
			const fallbackResponse = await fetchJson<ProductsApiResponse>(
				`/products?keywords=*&tags=${encodeURIComponent(normalizedTag)}`,
			)
			rawProducts = fallbackResponse?.products || []
		}



		const mappedProducts = await Promise.all(
			rawProducts.map(async (raw) => {
				const product = mapRawProduct(raw)
				if (product.stockCount === undefined && raw.uid) {
					const stockData = await fetchProductStock(raw.uid)
					if (stockData) {
						product.stockCount = stockData.stockCount
						if (stockData.unlimited !== undefined) {
							product.unlimited = stockData.unlimited
						}
					}
				}
				return product
			}),
		)

		productFamilyCache.set(normalizedTag, {
			products: mappedProducts,
			timestamp: Date.now(),
		})

		return mappedProducts
	} catch (error) {
		console.error(`[API] fetchProductFamily failed for ${fatherTag}:`, error)
		return []
	}
}

export interface SubmitOrderPayload {
	customer: {
		tsCommerceId?: string
		fullName: string
		email: string
		phone?: string
		vatNumber?: string
		address?: string
		city?: string
		zip?: string
		state?: string
		country?: string
	}
	items: Array<{
		productUid: number
		sku: string
		title: string
		quantity: number
		unitPrice: number
		options?: string
		customization?: string
		note?: string
		notes?: string
	}>
	paymentMethod: "transfer"
	notes?: string
	message?: string
	// ID of the authenticated agent submitting the order
	agent?: string
	couponCode?: string
	couponAmount?: number
	cashDiscountPercent?: number
	cashDiscountAmount?: number
}

export interface SubmitOrderResponse {
	id?: string
	orderId?: string
	tsCommerceOrderId?: string | null
	status?: string
	syncError?: string | null
}

export async function submitOrder(
	payload: SubmitOrderPayload,
): Promise<SubmitOrderResponse> {
	try {
		const url = `${API_BASE_URL}/orders`
		console.info("[API] submitOrder started:", { url, payload })

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		})

		const contentType = response.headers.get("content-type") ?? ""
		const responseText = await response.text()

		console.info("[API] submitOrder response received:", {
			status: response.status,
			statusText: response.statusText,
			contentType,
		})

		if (!response.ok) {
			console.error("[API] submitOrder failed:", {
				status: response.status,
				statusText: response.statusText,
				body: responseText.slice(0, 500),
			})
			throw new Error(`Failed to submit order: HTTP ${response.status}`)
		}

		try {
			return JSON.parse(responseText) as { orderId: string }
		} catch (error) {
			console.error("[API] submitOrder JSON parse failed:", {
				bodyPreview: responseText.slice(0, 500),
				error,
			})
			throw error
		}
	} catch (error) {
		console.error("[API] submitOrder failed:", error)
		throw error
	}
}

interface RawOrderDetail {
	orderId: string
	total: number
	orderDate: string
	status: number
	customerId?: string
	payment?: string
	paymentLabel?: string
	message?: string
	notes?: string
	billing: {
		fullname: string
		email: string
		phone?: string
		address1?: string
		city?: string
		stateName?: string
		zip?: string
		countryName?: string
	}
	items: Array<{
		productUid: string | number
		sku: string
		title: string
		quantity: number
		unitPrice: number
		options?: string
		customization?: string
		note?: string
		notes?: string
	}>
}

export async function fetchOrderById(
	id: string,
	knownCustomers?: ClientRecord[],
): Promise<OrderRecord | null> {
	try {
		const raw = await fetchJson<RawOrderDetail>(
			`/orders/${encodeURIComponent(id)}`,
		)
		console.info("[API] fetchOrderById success:", { id, raw })

		const customersList = knownCustomers ?? []
		const rawEmail = (raw.billing?.email || "").toLowerCase()
		const rawName = (raw.billing?.fullname || "").toLowerCase()

		let client = customersList.find((c) => c.email.toLowerCase() === rawEmail)
		if (!client && rawName) {
			client = customersList.find((c) => c.name.toLowerCase() === rawName)
		}

		const localOrder = getOrderById(id)
		const customerName = raw.billing?.fullname || localOrder?.customerName || ""
		const email = raw.billing?.email || localOrder?.email || ""
		const clientId = raw.customerId || (client ? client.id : (localOrder?.clientId || ""))
		const orderNotes =
			raw.message ||
			raw.notes ||
			(raw as any).customer_note ||
			(raw as any).client_note ||
			(raw as any).admin_note ||
			(raw as any).note ||
			localOrder?.notes ||
			localOrder?.message ||
			""

		return {
			id: raw.orderId,
			clientId,
			date: raw.orderDate ? raw.orderDate.slice(0, 10) : (localOrder?.date || ""),
			customerName,
			email,
			total: `€${(raw.total || 0).toFixed(2)}`,
			notes: orderNotes,
			message: orderNotes,
			customerDiscountPercent:
				client?.discountPercent ?? localOrder?.customerDiscountPercent,
			customerTags: client?.tags ?? localOrder?.customerTags,
			customerGroupName: client?.groupName ?? localOrder?.customerGroupName,
			cashDiscountPercent:
				extractCashDiscountPercent(client?.tags) ??
				extractCashDiscountPercent(localOrder?.customerTags) ??
				localOrder?.cashDiscountPercent,
			paymentTag:
				(client?.tags ? extractCustomerTagValue(client.tags, "payment") : undefined) ||
				localOrder?.paymentTag,
			paydescriptionTag:
				(client?.tags ? extractCustomerTagValue(client.tags, "paydescription") : undefined) ||
				localOrder?.paydescriptionTag,
			items: (raw.items || []).map((item, idx) => {
				let customizationNote: string | undefined = undefined
				if (item.customization && typeof item.customization === "string") {
					try {
						const parsedCust = JSON.parse(item.customization)
						if (parsedCust && typeof parsedCust === "object" && parsedCust.note) {
							customizationNote = String(parsedCust.note).trim()
						}
					} catch {
						if (
							!item.customization.startsWith("Colore:") &&
							!item.customization.startsWith("Temperatura:") &&
							!item.customization.startsWith("Misura:") &&
							item.customization !== "Standard"
						) {
							customizationNote = item.customization.trim()
						}
					}
				}

				const optionsStr = item.options || ""
				const parsed = parseItemOptions(optionsStr)

				let localItem = localOrder?.items[idx]
				if (localItem && localItem.code !== item.sku && localItem.sku !== item.sku) {
					localItem =
						localOrder?.items.find(
							(li) =>
								(li.code === item.sku || li.sku === item.sku) &&
								(li.options === optionsStr || li.note === customizationNote),
						) ||
						localOrder?.items.find(
							(li) => li.code === item.sku || li.sku === item.sku,
						)
				}

				const hasParsedAttributes = Boolean(
					parsed.attributes.color ||
						parsed.attributes.temperature ||
						parsed.attributes.size,
				)
				const hasLocalAttributes =
					Boolean(
						localItem?.attributes?.color &&
							localItem.attributes.color !== "Standard" &&
							localItem.attributes.color !== "",
					) ||
					Boolean(
						localItem?.attributes?.temperature &&
							localItem.attributes.temperature !== "Standard" &&
							localItem.attributes.temperature !== "",
					) ||
					Boolean(
						localItem?.attributes?.size &&
							localItem.attributes.size !== "Standard" &&
							localItem.attributes.size !== "",
					)

				const attributes = hasParsedAttributes
					? parsed.attributes
					: hasLocalAttributes
					? localItem!.attributes
					: {
							color: "",
							temperature: "",
							size: "",
					  }

				const uniqueItemId = item.productUid
					? `${item.productUid}_${idx}`
					: `${item.sku || "item"}_${idx}`

				const resolvedNote =
					customizationNote ||
					item.note ||
					item.notes ||
					parsed.note ||
					localItem?.note ||
					undefined

				return {
					id: uniqueItemId,
					code: item.sku || item.title || "",
					title: item.title || localItem?.title,
					price: item.unitPrice || 0,
					quantity: item.quantity || 1,
					note: resolvedNote,
					options: optionsStr || localItem?.options,
					imageUrl: localItem?.imageUrl,
					tags: localItem?.tags,
					stockCount: localItem?.stockCount,
					unlimited: localItem?.unlimited,
					attributes,
				}
			}),
			status: raw.status,
		}
	} catch (error) {
		console.error(`[API] fetchOrderById failed for ID ${id}:`, error)
		throw error
	}
}

export interface SubmitCustomerPayload {
	fullName: string
	email: string
	phone?: string
	address?: string
	city?: string
	zip?: string
	state?: string
	note?: string
	notes?: string
	code?: string
	taxNumber?: string
	tax_number?: string
	province?: string
	country?: string
	group?: string
	groupName?: string
	tags?: string[]
}

export async function updateCustomer(
	id: string,
	payload: SubmitCustomerPayload,
): Promise<void> {
	try {
		const url = `${API_BASE_URL}/customers/${encodeURIComponent(id)}`

		// Build rich payload compatible with Storeden Connect schema
		const countryVal = payload.country || payload.state || "Italia"
		const stateVal = payload.province || payload.state || ""

		const bodyPayload = {
			...payload,
			postalCode: payload.zip,
			postal_code: payload.zip,
			taxNumber: payload.taxNumber || payload.code,
			tax_number: payload.taxNumber || payload.code,
			code: payload.code || payload.taxNumber,
			note: payload.note || payload.notes,
			notes: payload.notes || payload.note,
			state: stateVal || countryVal,
			country: countryVal,
			province: payload.province || stateVal,
			addresses: [
				{
					fullname: payload.fullName,
					address1: payload.address || "",
					city: payload.city || "",
					zip: payload.zip || "",
					state: stateVal,
					province: stateVal,
					country: countryVal,
					phone: payload.phone || "",
					principal: true,
				},
			],
		}

		console.info("[API] updateCustomer started:", {
			url,
			payload: bodyPayload,
		})

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(bodyPayload),
		})

		const responseText = await response.text()

		if (!response.ok) {
			console.error("[API] updateCustomer failed:", {
				status: response.status,
				statusText: response.statusText,
				body: responseText.slice(0, 500),
			})
			throw new Error(`Failed to update customer: HTTP ${response.status}`)
		}

		console.info("[API] updateCustomer success:", {
			id,
			response: responseText.slice(0, 500),
		})

		// Save in local customer index for instant UI reflection
		const entry = {
			tags: payload.tags,
			city: payload.city,
			zip: payload.zip,
			address: payload.address,
			phone: payload.phone,
			code: payload.code || payload.taxNumber,
			province: payload.province || payload.state,
			state: payload.state || payload.province,
			country: payload.country || "Italia",
		}
		saveLocalCustomerIndexEntry(id, entry)
		if (payload.email) {
			saveLocalCustomerIndexEntry(payload.email.toLowerCase().trim(), entry)
		}
	} catch (error) {
		console.error("[API] updateCustomer failed:", error)
		throw error
	}
}

export async function submitCustomer(
	payload: SubmitCustomerPayload,
): Promise<{ client: { id: string } }> {
	try {
		const url = `${API_BASE_URL}/customers`
		console.info("[API] submitCustomer started:", { url, payload })

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		})

		const contentType = response.headers.get("content-type") ?? ""
		const responseText = await response.text()

		console.info("[API] submitCustomer response received:", {
			status: response.status,
			statusText: response.statusText,
			contentType,
		})

		if (!response.ok) {
			console.error("[API] submitCustomer failed:", {
				status: response.status,
				statusText: response.statusText,
				body: responseText.slice(0, 500),
			})
			throw new Error(`Failed to submit customer: HTTP ${response.status}`)
		}

		let result: { client: { id: string } }
		try {
			result = JSON.parse(responseText) as { client: { id: string } }
		} catch (error) {
			console.error("[API] submitCustomer JSON parse failed:", {
				bodyPreview: responseText.slice(0, 500),
				error,
			})
			throw error
		}

		if (result?.client?.id) {
			// Save locally immediately
			const entry = {
				tags: payload.tags,
				city: payload.city,
				zip: payload.zip,
				address: payload.address,
				phone: payload.phone,
				code: payload.code || payload.taxNumber,
				province: payload.province || payload.state,
				state: payload.state || payload.province,
				country: payload.country || "Italia",
			}
			saveLocalCustomerIndexEntry(result.client.id, entry)
			if (payload.email) {
				saveLocalCustomerIndexEntry(payload.email.toLowerCase().trim(), entry)
			}

			try {
				console.info(
					"[API] submitCustomer workaround: updating all fields via PUT",
					result.client.id,
				)
				await updateCustomer(result.client.id, payload)
			} catch (err) {
				console.warn("[API] submitCustomer workaround PUT failed:", err)
			}
		}

		return result
	} catch (error) {
		console.error("[API] submitCustomer failed:", error)
		throw error
	}
}

export async function deleteCustomer(id: string): Promise<void> {
	try {
		const url = `${API_BASE_URL}/customers/${encodeURIComponent(id)}`
		console.info("[API] deleteCustomer started:", { url })

		const response = await fetch(url, {
			method: "DELETE",
		})

		if (!response.ok) {
			const responseText = await response.text()
			console.error("[API] deleteCustomer failed:", {
				status: response.status,
				statusText: response.statusText,
				body: responseText.slice(0, 500),
			})
			throw new Error(`Failed to delete customer: HTTP ${response.status}`)
		}

		console.info("[API] deleteCustomer success:", { id })
	} catch (error) {
		console.error("[API] deleteCustomer failed:", error)
		throw error
	}
}

export interface AlyanteAgent {
	code: string
	name: string
}

interface AlyanteLookupResponse {
	name?: string
	pageNumber?: number
	pageSize?: number
	totalCount?: number
	data?: Array<[string, string, ...unknown[]]>
}

/**
 * Fetches all agents from Alyante ERP WebAPI (paginating page 1 and page 2 to get all agents).
 */
export async function fetchAlyanteAgents(): Promise<AlyanteAgent[]> {
	// Direct browser calls to webapi.gealuce.com fail with 401 Unauthorized because
	// it is a private ERP endpoint requiring internal credentials.
	// The agent profile is already fully resolved from Storeden customer data and tags.
	return []
}

/**
 * Fetches and prints all Alyante agents to the browser console in a neat table format.
 */
export async function logAllAgents(): Promise<AlyanteAgent[]> {
	try {
		const agents = await fetchAlyanteAgents()
		console.info(
			`%c[Alyante ERP] Elenco completo agenti (${agents.length} totali):`,
			"color: #E9E21B; font-weight: bold; background: #111; padding: 4px 8px; border-radius: 4px;",
		)
		console.table(
			agents.map((a) => ({
				"Codice Agente": a.code,
				"Nome / Ragione Sociale": a.name,
				Tag: `agente-${a.code}`,
			})),
		)
		return agents
	} catch (error) {
		console.error("[Alyante ERP] Error logging agents:", error)
		return []
	}
}
